import { SafeDictionary } from "@insite/client-framework/Common/Types";
import translate from "@insite/client-framework/Translate";
import { VariantTraitModel } from "@insite/client-framework/Types/ApiModels";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridItem from "@insite/mobius/GridItem";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import CENDropDownIcon from "@tCentro/Icons/CENDropDownIcon";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => ({
    cenVariantPdpAddToCartClicked: state.t_components.cenVariantPdpAddToCartIsClicked.variantPdpAddToCartClicked,
    selectedProductId: state.pages.productDetails.selectedProductId,
});
interface Props {
    variantSelection: SafeDictionary<string>;
    variantTrait: VariantTraitModel;
    updateVariantSelection: (traitValueId: string, variantTraitId: string) => void;
}

type CustomProps = Props & ReturnType<typeof mapStateToProps>;

export interface ProductDetailsVariantDropdownStyles {
    select?: SelectPresentationProps;
    errorSelectStyles?: SelectPresentationProps;
}

export const productDetailsVariantDropdownStyles: ProductDetailsVariantDropdownStyles = {
    select: {
        iconProps: {
            src: CENDropDownIcon,
            css: css`
                width: 28px !important;
                height: 28px !important;
                top: 12px !important;
                right: 24px !important;
            `,
        },
        cssOverrides: {
            formField: css`
                label {
                    font-size: ${TYPOGRAPHY.size.smaller};
                    font-weight: 600;
                    line-height: 16px;
                    text-align: left;
                    color: ${getProp("theme.typography.p.color")};
                    margin-left: 24px;
                    & + div {
                        margin-top: 2px !important;
                    }
                }
            `,
            formInputWrapper: css`
                select {
                    border: 2px solid ${COLORS.white} !important;
                    border-radius: 24px !important;
                    color: ${getProp("theme.typography.p.color")} !important;
                    padding: 12px 20px 12px 20px !important;
                    font-family: Poppins;
                    height: 49px !important;
                    font-size: ${TYPOGRAPHY.size.smaller};
                    font-weight: 400 !important;
                    line-height: 24px !important;
                    text-align: left;
                    @media (min-width: 992px) and (max-width: 1100px) {
                        padding: 12px 50px 12px 20px !important;
                    }
                    option {
                        color: ${getProp("theme.typography.p.color")};
                    }
                    option:disabled {
                        color: #b6b6b6;
                        opacity: 0.5;
                        cursor: not-allowed !important;
                    }
                }
            `,
        },
    },
    errorSelectStyles: {
        iconProps: {
            src: CENDropDownIcon,
            css: css`
                width: 28px !important;
                height: 28px !important;
                top: 12px !important;
                right: 24px !important;
            `,
        },
        cssOverrides: {
            formField: css`
                label {
                    font-size: ${TYPOGRAPHY.size.smaller};
                    font-weight: 600;
                    line-height: 16px;
                    text-align: left;
                    color: ${getProp("theme.typography.p.color")};
                    margin-left: 24px;

                    & + div {
                        margin-top: 2px !important;
                    }
                }
            `,
            formInputWrapper: css`
                select {
                    border: 2px solid #ed0000 !important;
                    border-radius: 24px !important;
                    color: #ed0000 !important;
                    padding: 12px 20px 12px 20px !important;
                    height: 49px !important;
                    font-size: ${TYPOGRAPHY.size.smaller};
                    font-weight: 500 !important;
                    line-height: 24px !important;
                    text-align: left;
                    font-family: Poppins;

                    @media (min-width: 992px) and (max-width: 1100px) {
                        padding: 12px 50px 12px 20px !important;
                    }
                    option {
                        color: ${getProp("theme.typography.p.color")};
                    }
                    option:disabled {
                        color: #b6b6b6;
                        opacity: 0.5;
                        cursor: not-allowed !important;
                    }
                }
            `,
        },
    },
};

const styles = productDetailsVariantDropdownStyles;

const ProductDetailsVariantDropdown = ({
    variantSelection,
    updateVariantSelection,
    variantTrait,
    cenVariantPdpAddToCartClicked,
}: CustomProps) => {
    return (
        <Select
            {...(cenVariantPdpAddToCartClicked && variantSelection[variantTrait.id] === ""
                ? styles.errorSelectStyles
                : styles.select)}
            key={variantTrait.id}
            label={variantTrait.nameDisplay}
            value={variantSelection[variantTrait.id]}
            onChange={event => {
                updateVariantSelection(event.currentTarget.value, variantTrait.id);
            }}
            data-test-selector={`styleSelect_${variantTrait.name}`}
            required={true}
        >
            <option value="" key={variantTrait.id}>
                {variantTrait.unselectedValue
                    ? variantTrait.unselectedValue
                    : `${translate("Select")} ${variantTrait.nameDisplay}`}
            </option>
            {variantTrait.traitValues?.map(traitValue => (
                <option value={`${traitValue.id}`} key={`${traitValue.id}`} disabled={traitValue.isDisabled}>
                    {traitValue.valueDisplay}
                </option>
            ))}
        </Select>
    );
};

export default connect(mapStateToProps)(ProductDetailsVariantDropdown);
